import { Component, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { ListaAcaoEngrenagem } from '../components/models/lista-acao-engrenagem.model';
import { ListaColunaConfig } from '../components/models/lista-coluna-config.model';
import { ExportacaoConfig, ListBoxComponent } from '../components/list-box/list-box.component';
import { AutorizacaoManualModel, toAutorizacaoManualModel } from '../model/autorizacao-manual.model';
import { LoginModel } from '../model/login.model';
import { ListaParametrosPaginacao } from '../components/models/lista-paginacao.model';
import { SwapService } from '../services/swap.service';
import {
  SwapImagemTipo,
  SwapSessaoConsultaFiltro,
  SwapSessaoOrdenacao,
  montarDataUrlImagemSessao,
} from '../model/swap';
import {
  VisualizadorImagemDialogComponent,
  VisualizadorImagemDialogData,
} from '../components/visualizador-imagem-dialog/visualizador-imagem-dialog.component';
import { FlashMessageService } from '../services/flash-message.service';
import { AuthenticationService } from '../services/authentication.service';
import { SnackBarService } from '../services/snack-bar-service.service';
import { PesquisaMotoristaAutorizacaoManualComponent } from '../motorista/pesquisa-motorista-autorizacao-manual/pesquisa-motorista-autorizacao-manual.component';
import { criarFiltroInicialPesquisaMotoristaAutorizacaoManual } from '../motorista/pesquisa-motorista-autorizacao-manual/motorista-pesquisa-autorizacao-manual.config';
import { criarAcoesAutorizacaoManualListaRotas } from './autorizacao-manual-acoes';
import { AUTORIZACAO_MANUAL_COLUNAS } from './autorizacao-manual-colunas.config';

/** `YYYY-MM-DD` no fuso local, N dias antes da data de hoje (00:00 local). */
function isoDateLocalMenosDias(diasAtras: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - diasAtras);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** `YYYY-MM-DD` da data de hoje no fuso local. */
function isoDateLocalHoje(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

@Component({
  selector: 'app-autorizacao-manual',
  templateUrl: './autorizacao-manual.component.html',
  styleUrls: ['./autorizacao-manual.component.scss'],
})
export class AutorizacaoManualComponent implements OnInit, OnDestroy {
  readonly tituloLocal = 'Autorização Manual';

  @ViewChild('listBox') listBox!: ListBoxComponent;

  /** Dados do usuário logado para aplicar aparência/tema. */
  usuario: LoginModel | undefined;

  /** Controla a exibição do spinner de loading. */
  loading = 0;

  /** Ligado ao FilterBox: controla o painel de filtros expandido ou recolhido. */
  filtroAberto = false;

  /** Estado dos filtros (UI). Convertido em `SwapSessaoConsultaFiltro` ao chamar a API. */
  filtros: {
    dataInicio: string;
    /** Fim do intervalo de data de criação (inclusivo). */
    dataCriacaoAte: string;
    motoristaCodigo: number | null;
    motoristaApelido: string;
    placa: string;
    autorizado: 'todos' | 'sim' | 'nao';
    abastecimento: 'todos' | 'sim' | 'nao';
  } = {
    dataInicio: isoDateLocalMenosDias(30),
    dataCriacaoAte: isoDateLocalHoje(),
    motoristaCodigo: null,
    motoristaApelido: '',
    placa: '',
    autorizado: 'todos',
    abastecimento: 'todos',
  };

  readonly opcoesTamanhoPaginaLista: number[] = [10, 20, 50, 100];

  itens: AutorizacaoManualModel[] = [];
  totalRegistros = 0;
  paginaAtual = 1;
  tamanhoPagina = 10;

  /** Coluna de ordenação enviada à API (default: 'DTH_CRIACAO' = coluna "Data"). */
  ordenacao: SwapSessaoOrdenacao = 'DTH_CRIACAO';
  /** Direção da ordenação. true = DESC (default), false = ASC. */
  ordenacaoDesc = true;

  readonly colunas = AUTORIZACAO_MANUAL_COLUNAS;
  readonly acoesEngrenagem: ListaAcaoEngrenagem<AutorizacaoManualModel>[];

  /** Emite após mutações (bypass na engrenagem) para recarregar a lista. */
  private readonly mudancas = new Subject<void>();
  private sub?: Subscription;

  /**
   * Cache de **1 slot** — só a última imagem carregada com sucesso é
   * mantida. Ao carregar uma nova (chave diferente), a anterior é
   * sobrescrita (descartada) implicitamente. Reabrir a **mesma** imagem
   * em sequência aproveita o cache; trocar de imagem força nova
   * requisição. Vive enquanto a página está montada.
   *
   * `key = `${dscSessionExterna}|${tipo}`.
   */
  private ultimaImagemCarregada: { key: string; dataUrl: string } | null = null;

  constructor(
    private swapService: SwapService,
    private dialog: MatDialog,
    private flash: FlashMessageService,
    private router: Router,
    private authService: AuthenticationService,
    private snackBar: SnackBarService,
    private renderer: Renderer2,
  ) {
    this.acoesEngrenagem = [
      ...criarAcoesAutorizacaoManualListaRotas(
        this.router,
        this.swapService,
        this.flash,
        () => this.usuario?.retorno?.guid ?? '',
        () => {
          this.mudancas.next();
          this.carregarPagina();
        },
        () => this.loading++,
        () => this.loading--,
      ),
      this.criarAcaoVerImagem(
        'verImgBomba',
        'Ver imagem da bomba',
        'BOMBA',
        (l) => l.flgImgBomba,
      ),
      this.criarAcaoVerImagem(
        'verImgPainel',
        'Ver imagem do painel',
        'PANEL',
        (l) => l.flgImgPainel,
      ),
      this.criarAcaoVerImagem(
        'verImgPlaca',
        'Ver imagem da placa',
        'PLACA',
        (l) => l.flgImgPlaca,
      ),
    ];
  }

  /**
   * Helper para criar uma ação de "Ver imagem ..." no menu de
   * engrenagem. Visível apenas quando a flag respectiva for `true`
   * (oculta o item em vez de desabilitar — não polui o menu).
   * Reusa a mesma lógica de carregamento/cache da coluna de ícones.
   */
  private criarAcaoVerImagem(
    id: string,
    rotulo: string,
    tipo: SwapImagemTipo,
    temImagem: (l: AutorizacaoManualModel) => boolean,
  ): ListaAcaoEngrenagem<AutorizacaoManualModel> {
    return {
      id,
      rotulo,
      exibicao: (item) => (temImagem(item) ? 'habilitado' : 'oculto'),
      aoExecutar: (item) => this.verImagemSessao(item, tipo),
    };
  }

  ngOnInit(): void {
    this.loading++;
    this.usuario = this.authService.getDadosUserLocal();

    this.authService
      .validate(this.usuario?.retorno?.guid ?? '')
      .then((dados) => {
        if (!dados.sucesso) {
          this.snackBar.flashMessage(
            this.authService.msgSessaoExpirada,
            ['snackbar-error'],
            'Fechar',
            0,
          );
          this.usuario = undefined;
        } else {
          this.iniciar();
        }
      })
      .catch(() => {
        this.snackBar.flashMessage(
          this.authService.msgSessaoExpirada,
          ['snackbar-error'],
          'Fechar',
          0,
        );
      })
      .finally(() => {
        this.loading--;
      });
  }

  private iniciar(): void {
    this.renderer.addClass(
      document.body,
      this.usuario?.retorno?.aparencia ?? 'PADRAO',
    );
    this.carregarPagina();
    this.sub = this.mudancas.subscribe(() => this.carregarPagina());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  alternarFiltro(): void {
    this.flash.limpar();
    this.filtroAberto = !this.filtroAberto;
  }

  aplicarFiltroServidor(): void {
    this.flash.limpar();
    this.paginaAtual = 1;
    this.carregarPagina();
  }

  limparFiltros(): void {
    this.filtros = {
      dataInicio: isoDateLocalMenosDias(30),
      dataCriacaoAte: isoDateLocalHoje(),
      motoristaCodigo: null,
      motoristaApelido: '',
      placa: '',
      autorizado: 'todos',
      abastecimento: 'todos',
    };
    this.paginaAtual = 1;
    this.carregarPagina();
  }

  abrirBuscaMotorista(): void {
    const filtroMotorista = criarFiltroInicialPesquisaMotoristaAutorizacaoManual(
      this.usuario?.retorno?.guid ?? '',
    );
    const dialogRef = this.dialog.open(PesquisaMotoristaAutorizacaoManualComponent, {
      width: '800px',
      height: '700px',
      data: { dataKey: filtroMotorista },
    });
    dialogRef.afterClosed().subscribe((response) => {
      if (response?.data) {
        const cod =
          typeof response.data.codigo === 'number'
            ? response.data.codigo
            : Number(response.data.codigo) || null;
        this.filtros.motoristaCodigo = cod;
        this.filtros.motoristaApelido =
          response.data.apelido || response.data.nome || '';
        this.aplicarFiltroServidor();
      }
    });
  }

  limparMotorista(): void {
    this.filtros.motoristaCodigo = null;
    this.filtros.motoristaApelido = '';
    this.aplicarFiltroServidor();
  }

  limparFiltroDataInicio(): void {
    this.filtros.dataInicio = '';
  }

  limparFiltroDataCriacaoAte(): void {
    this.filtros.dataCriacaoAte = '';
  }

  limparFiltroPlacaCampo(): void {
    this.filtros.placa = '';
  }

  onPaginacaoAlterada(params: ListaParametrosPaginacao): void {
    this.flash.limpar();
    const p = Math.floor(Number(params.pagina));
    const t = Math.floor(Number(params.tamanhoPagina));
    this.paginaAtual = Number.isFinite(p) && p >= 1 ? p : 1;
    let size = Number.isFinite(t) && t >= 1 ? t : this.tamanhoPagina;
    if (!this.opcoesTamanhoPaginaLista.includes(size)) {
      size = 10;
    }
    this.tamanhoPagina = size;
    this.carregarPagina();
  }

  onBotaoAcao(evento: { linha: unknown; coluna: ListaColunaConfig }): void {
    if (evento.coluna.id === 'acao') {
      const acao = this.acoesEngrenagem.find((a) => a.id === 'bypass');
      acao?.aoExecutar(evento.linha as AutorizacaoManualModel);
    }
  }

  /**
   * Recebe o clique em um dos ícones da coluna `'imgs'`
   * (`modoCelula: 'iconesImagens'`). Apenas delega para
   * {@link verImagemSessao}, que é reusada também pelas ações
   * "Ver imagem ..." do menu de engrenagem.
   */
  onIconeImagemClicado(evento: {
    linha: unknown;
    coluna: ListaColunaConfig;
    iconeId: string;
  }): void {
    this.verImagemSessao(
      evento.linha as AutorizacaoManualModel,
      evento.iconeId as SwapImagemTipo,
    );
  }

  /**
   * Carrega (ou pega do cache) e exibe a imagem indicada da sessão num
   * `VisualizadorImagemDialogComponent` centralizado, com zoom in/out
   * + scroll nativo.
   *
   * Reusada por:
   *  - clique no ícone da coluna `'imgs'` (`onIconeImagemClicado`);
   *  - itens "Ver imagem ..." do menu de engrenagem.
   *
   * Cenários de retorno do `obterImagemSessao`:
   *  - `!sucesso`              → flash erro genérico.
   *  - `retorno == null`       → flash aviso "sessão não encontrada".
   *  - `retorno.imgBase64 == null` → flash aviso "imagem indisponível"
   *    (a flag pode estar desatualizada; raro).
   *  - sucesso com imagem      → cache + abrir dialog.
   */
  private verImagemSessao(
    linha: AutorizacaoManualModel,
    tipo: SwapImagemTipo,
  ): void {
    const titulo = this.tituloImagemDialog(tipo, linha);
    const cacheKey = `${linha.dscSessionExterna}|${tipo}`;

    if (this.ultimaImagemCarregada?.key === cacheKey) {
      this.abrirDialogImagem(titulo, this.ultimaImagemCarregada.dataUrl);
      return;
    }

    const guid = this.usuario?.retorno?.guid ?? '';
    this.loading++;
    this.swapService
      .obterImagemSessao(guid, linha.dscSessionExterna, tipo)
      .subscribe({
        next: (res) => {
          this.loading--;
          if (!res?.sucesso) {
            this.flash.erro('Erro ao carregar imagem.');
            return;
          }
          if (!res.retorno) {
            this.flash.aviso('Sessão não encontrada ou sem permissão.');
            return;
          }
          const dataUrl = montarDataUrlImagemSessao(res.retorno);
          if (!dataUrl) {
            this.flash.aviso('Imagem indisponível para esta sessão.');
            return;
          }
          /*
           * Sobrescreve o slot único: a imagem anterior (se houver) é
           * descartada agora — só nesse momento, depois de termos a
           * nova carregada com sucesso. Se a request falhar, o cache
           * antigo permanece intacto.
           */
          this.ultimaImagemCarregada = { key: cacheKey, dataUrl };
          this.abrirDialogImagem(titulo, dataUrl);
        },
        error: (err) => {
          this.loading--;
          console.error(err);
          this.flash.erro('Erro ao carregar imagem.');
        },
      });
  }

  private abrirDialogImagem(titulo: string, dataUrl: string): void {
    const dialogData: VisualizadorImagemDialogData = { titulo, dataUrl };
    this.dialog.open(VisualizadorImagemDialogComponent, {
      data: dialogData,
      maxWidth: '95vw',
      maxHeight: '95vh',
    });
  }

  private tituloImagemDialog(
    tipo: SwapImagemTipo,
    linha: AutorizacaoManualModel,
  ): string {
    const rotulos: Record<SwapImagemTipo, string> = {
      BOMBA: 'Imagem da bomba',
      PANEL: 'Imagem do painel (odômetro)',
      PLACA: 'Imagem da placa',
    };
    const sufixo =
      linha.numero != null
        ? ` — #${linha.numero}`
        : linha.codSessao != null
        ? ` — sessão ${linha.codSessao}`
        : '';
    return `${rotulos[tipo]}${sufixo}`;
  }

  /**
   * Recebe mudanças de ordenação do ListBox (combo, radio asc/desc ou
   * clique no cabeçalho) e recarrega a página com a nova ordenação.
   */
  onOrdenacaoAlterada(evento: {
    idColuna: string;
    valorBackend?: string;
    desc: boolean;
  }): void {
    if (evento.valorBackend) {
      this.ordenacao = evento.valorBackend as SwapSessaoOrdenacao;
    }
    this.ordenacaoDesc = evento.desc;
    this.paginaAtual = 1;
    this.carregarPagina();
  }

  onIncluir(): void {
    void this.router.navigate(['/autorizacao-manual', 'incluir']);
  }

  onExcel(): void {
    const now = new Date();
    const nomeArquivo = `AutorizacaoManual_${now.toISOString().slice(0, 10)}`;
    this.listBox.abrirDialogoExportacao(nomeArquivo, 'CTF');
  }

  onExportarTodos(config: ExportacaoConfig): void {
    const guid = this.usuario?.retorno?.guid ?? '';
    const filtro = this.montarFiltroSwap(null, null);
    this.loading++;
    this.swapService.listarSessoes(guid, filtro).subscribe({
      next: (res) => {
        this.loading--;
        if (!res?.sucesso || !res.retorno) {
          const msg =
            res?.mensagens?.find((m) => (m ?? '').trim()) ??
            'Erro ao exportar dados.';
          this.flash.erro(msg);
          return;
        }
        const dados = res.retorno.itens.map((s) => toAutorizacaoManualModel(s));
        this.listBox.exportarDados(
          dados,
          config.nomeArquivo,
          config.autor,
          config.formato,
        );
        this.flash.sucesso(`Exportados ${dados.length} registros.`);
      },
      error: (err) => {
        this.loading--;
        console.error(err);
        this.flash.erro(
          this.formatarErroHttp(err, 'Erro ao exportar dados.'),
        );
      },
    });
  }

  private carregarPagina(): void {
    const guid = this.usuario?.retorno?.guid ?? '';
    const filtro = this.montarFiltroSwap(this.paginaAtual, this.tamanhoPagina);
    this.swapService.listarSessoes(guid, filtro).subscribe({
      next: (res) => {
        if (!res?.sucesso || !res.retorno) {
          this.totalRegistros = 0;
          this.itens = [];
          const msgLista =
            res?.mensagens?.find((m) => (m ?? '').trim()) ??
            'Não foi possível carregar a lista.';
          this.flash.erro(msgLista);
          return;
        }
        this.totalRegistros = res.retorno.totalDeItens;
        this.itens = res.retorno.itens.map((s) => toAutorizacaoManualModel(s));
      },
      error: (err) => {
        console.error(err);
        this.totalRegistros = 0;
        this.itens = [];
        this.flash.erro(
          this.formatarErroHttp(
            err,
            'Não foi possível carregar a lista. Tente novamente.',
          ),
        );
      },
    });
  }

  /**
   * Extrai mensagem legível de erros HTTP do `listarSessoes` (string,
   * array de strings do backend, ProblemDetails, etc.).
   */
  private formatarErroHttp(err: unknown, fallback: string): string {
    const e = err as { error?: unknown; message?: string };
    const body = e?.error;

    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    if (Array.isArray(body) && body.length) {
      const msgs = body
        .map((m) =>
          typeof m === 'string' ? m : (m as { toString?: () => string })?.toString?.() ?? '',
        )
        .filter(Boolean);
      if (msgs.length) {
        return msgs.join(' | ');
      }
    }

    if (body && typeof body === 'object') {
      const obj = body as {
        dscRetorno?: string;
        message?: string;
        title?: string;
      };
      if (obj.dscRetorno) {
        return obj.dscRetorno;
      }
      if (obj.message) {
        return obj.message;
      }
      if (obj.title) {
        return obj.title;
      }
    }

    return e?.message || fallback;
  }

  private montarFiltroSwap(
    pageNumber: number | null,
    rowsPage: number | null,
  ): SwapSessaoConsultaFiltro {
    const sim = (v: 'todos' | 'sim' | 'nao'): 'SIM' | 'NAO' | null =>
      v === 'sim' ? 'SIM' : v === 'nao' ? 'NAO' : null;
    return {
      dthCriacaoDe: this.diaParaInicioDiaFiltro(this.filtros.dataInicio),
      dthCriacaoAte: this.diaParaInicioDiaFiltro(this.filtros.dataCriacaoAte),
      codMotorista: this.filtros.motoristaCodigo ?? null,
      dscPlaca: this.filtros.placa?.trim() || null,
      dscAutorizado: sim(this.filtros.autorizado),
      dscAbastecimentoRealizado: sim(this.filtros.abastecimento),
      ordenacao: this.ordenacao,
      ordenacaoDesc: this.ordenacaoDesc,
      pageNumber,
      rowsPage,
    };
  }

  /**
   * Envia início do dia sem sufixo Z para evitar DateTime Utc + conversão SQL
   * que deslocava a hora e quebrava o filtro por DTH_CRIACAO na homolog.
   */
  private diaParaInicioDiaFiltro(dia: string | null | undefined): string | null {
    if (!dia) {
      return null;
    }
    return `${dia}T00:00:00`;
  }
}
